import { ChangesetOpDTO } from "../models/ChangeRequestDTO";

/** Page id → title, for describing the pages an op touches. */
export type PageTitles = Record<number, string>;

function pageLabel(id: number | undefined, titles: PageTitles, fallbackTitle?: string): string {
    if (id === undefined || id === null) return fallbackTitle ? `"${fallbackTitle}"` : "(unknown page)";
    const title = titles[id] ?? fallbackTitle;
    return title ? `#${id} "${title}"` : `#${id}`;
}

function parentLabel(parentRef: string | null | undefined, ops: ChangesetOpDTO[], titles: PageTitles): string {
    if (parentRef === undefined || parentRef === null || parentRef === "") return "the space root";
    if (parentRef.startsWith("new:")) {
        const tempId = parentRef.substring(4);
        const created = ops.find((o) => o.op === "create" && o.tempId === tempId);
        return created?.title ? `new page "${created.title}"` : `new page ${tempId}`;
    }
    const id = Number(parentRef);
    return Number.isFinite(id) ? pageLabel(id, titles) : parentRef;
}

/** What an update changes: body, rules, title (in that order). */
export function updatedFields(op: ChangesetOpDTO): string[] {
    const fields: string[] = [];
    if (op.content !== undefined && op.content !== null) fields.push("body");
    else if (op.clearContent) fields.push("body (cleared)");
    if (op.rules) fields.push("rules");
    if (op.title !== undefined && op.title !== null) fields.push("title");
    return fields;
}

/**
 * One line per op, in the reviewer's words:
 * `create "<title>" under <parent>`, `update #<id> "<title>": body, rules`, `reorder #<id> under <parent> at <n>`, `delete #<id>`.
 */
export function describeOp(op: ChangesetOpDTO, ops: ChangesetOpDTO[], titles: PageTitles): string {
    switch (op.op) {
        case "create":
            return `create "${op.title ?? "(untitled)"}" under ${parentLabel(op.parentRef, ops, titles)}`;
        case "update": {
            const fields = updatedFields(op);
            return `update ${pageLabel(op.id, titles, op.title ?? undefined)}${fields.length ? `: ${fields.join(", ")}` : ""}`;
        }
        case "reorder": {
            const at = op.index !== undefined && op.index !== null ? ` at position ${op.index}` : "";
            return `reorder ${pageLabel(op.id, titles)} under ${parentLabel(op.parentRef, ops, titles)}${at}`;
        }
        case "delete":
            return `delete ${pageLabel(op.id, titles)}`;
        case "upload":
            return `upload ${op.name ?? "(file)"}${op.pageRef ? ` to ${pageLabel(op.pageRef, titles)}` : ""}`;
        default:
            return `${op.op} ${op.id !== undefined ? pageLabel(op.id, titles) : ""}`.trim();
    }
}
